import type { TFunction } from "i18next"
import React, { createContext, useContext, useMemo, useState } from "react"

import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"

import { useAppContext } from "@contexts/app_context"
import useControlledForm from "@hooks/use_controlled_form"
import useToggle from "@hooks/use_toggle"
import i18next from "@utils/localization"
import {
  AiMessageStatusEnum,
  AiRunKindFilterEnum,
  ChannelType,
  GetCustomerOwnersData
} from "__generated__/graphql"
import type {
  Control,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form"

type AiAnswersPageContextProps = {
  translate: TFunction<"translation", undefined>
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  control: Control<AiAnswersFormData>
  watch: UseFormWatch<AiAnswersFormData>
  setValue: UseFormSetValue<AiAnswersFormData>
  handleSubmit: UseFormHandleSubmit<AiAnswersFormData, AiAnswersFormData>
  selectedCustomer?: GetCustomerOwnersData | null
  setSelectedCustomer?: (customer: GetCustomerOwnersData | null) => void
  filters: AiAnswersFormData
  setFilters: React.Dispatch<React.SetStateAction<AiAnswersFormData>>
  resetFilters: () => void
}
export type AiAnswersFormData = {
  customerId?: string | undefined
  channelType?: ChannelType | ""
  status?: AiMessageStatusEnum | ""
  kind?: AiRunKindFilterEnum | ""
  startDate?: Date
  endDate?: Date
}

function createDefaultFilterValues(): AiAnswersFormData {
  return {
    customerId: "",
    channelType: "",
    status: "",
    kind: "",
    startDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    endDate: new Date()
  }
}

const defaultValues: AiAnswersPageContextProps = {
  translate: i18next.t,
  control: {} as Control<AiAnswersFormData>,
  watch: {} as UseFormWatch<AiAnswersFormData>,
  setValue: {} as UseFormSetValue<AiAnswersFormData>,
  handleSubmit: {} as UseFormHandleSubmit<AiAnswersFormData, AiAnswersFormData>,
  filters: {} as AiAnswersFormData,
  setFilters: {} as React.Dispatch<React.SetStateAction<AiAnswersFormData>>,
  resetFilters: () => {}
}

const AiAnswersPageContext = createContext(defaultValues)

export function AiAnswersPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useAppContext()
  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)
  const [selectedCustomer, setSelectedCustomer] =
    useState<GetCustomerOwnersData | null>(null)
  const initialFilters = useMemo(createDefaultFilterValues, [])

  const schema = yup
    .object({
      customerId: yup.string().optional(),
      channelType: yup.string().optional(),
      status: yup.string().optional(),
      kind: yup.string().optional(),
      startDate: yup.date().optional(),
      endDate: yup.date().optional()
    })
    .required()

  // Filters are view state, not unsaved work: guarding them made every conversation pick and
  // reload ask "leave this page?" once any filter had been touched.
  const { control, watch, setValue, handleSubmit } =
    useControlledForm<AiAnswersFormData>(
      {
        //@ts-expect-error yup types are not compatible with react-hook-form
        resolver: yupResolver(schema),
        mode: "all",
        defaultValues: initialFilters
      },
      false
    )
  const [filters, setFilters] = useState<AiAnswersFormData>(initialFilters)

  const resetFilters = () => {
    const fresh = createDefaultFilterValues()
    setFilters(fresh)
    setSelectedCustomer(null)
    setValue("customerId", fresh.customerId)
    setValue("channelType", fresh.channelType)
    setValue("status", fresh.status)
    setValue("kind", fresh.kind)
    setValue("startDate", fresh.startDate)
    setValue("endDate", fresh.endDate)
  }

  return (
    <AiAnswersPageContext.Provider
      value={{
        ...defaultValues,
        translate,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        control,
        watch,
        setValue,
        handleSubmit,
        selectedCustomer,
        setSelectedCustomer,
        filters,
        setFilters,
        resetFilters
      }}
    >
      {props.children}
    </AiAnswersPageContext.Provider>
  )
}

export function useAiAnswersPageContext() {
  const context = useContext(AiAnswersPageContext)
  if (context === undefined) {
    throw new Error("UserPage Context Error")
  }
  return context
}
